import {
  AGREEMENT_RATE_TIME_BLOCKS,
  RateTimeBlock,
} from 'src/app/pages/agreements/constants/agreement-rate-time-block.constants';
import { CalculatedRateRateScheduledInput } from 'src/app/shared/graphql/agreements/calculated-rate-query.graphql';
import { TimeBlock } from 'src/app/shared/graphql/graphql-types';

/**
 * Finds the agreement rate time block that contains the constants such as name and description.
 *
 * @param timeBlock - The time block to find.
 * @returns The AGREEMENT_RATE_TIME_BLOCKS object.
 */
export function findRateTimeBlock(timeBlock: TimeBlock): RateTimeBlock {
  return AGREEMENT_RATE_TIME_BLOCKS.find((block: RateTimeBlock) => block.timeBlock === timeBlock);
}

/**
 * Gets the time block name.
 *
 * @param timeBlock - The time block - Ex: weekday_daytime.
 * @returns The time block name - Ex: Weekday daytime.
 */
export function getTimeBlockName(timeBlock: TimeBlock): RateTimeBlock['name'] {
  return findRateTimeBlock(timeBlock).name;
}

/**
 * Gets the list of default rate time blocks.
 *
 * @returns The array of default AGREEMENT_RATE_TIME_BLOCKS.
 */
export function getDefaultTimeBlocks(): RateTimeBlock[] {
  return AGREEMENT_RATE_TIME_BLOCKS.filter((rateTimeBlock) => rateTimeBlock.default);
}

/**
 * Evaluates a RateSchedule to identify all the time blocks that are selected.
 *
 * @param rateSchedule - The rate schedule to evaluate.
 */
export function getTimeBlocksFromRateSchedule(rateSchedule: Partial<CalculatedRateRateScheduledInput>): TimeBlock[] {
  const timeBlocks: TimeBlock[] = [];
  const { weekday, daytime, evening, overnight, saturday, sunday, publicHoliday } = rateSchedule;

  // Weekday
  if (weekday) {
    // If there are no time selected, add the all weekday timeblocks
    if (!daytime && !evening && !overnight) {
      timeBlocks.push(TimeBlock.WeekdayDaytime, TimeBlock.WeekdayEvening, TimeBlock.WeekdayOvernight);
    }

    // Weekday daytime
    if (daytime) {
      timeBlocks.push(TimeBlock.WeekdayDaytime);
    }

    // Weekday evening
    if (evening) {
      timeBlocks.push(TimeBlock.WeekdayEvening);
    }

    // Weekday overnight
    if (overnight) {
      timeBlocks.push(TimeBlock.WeekdayOvernight);
    }
  }

  // Saturday
  if (saturday) {
    timeBlocks.push(TimeBlock.Saturday);
  }

  // Sunday
  if (sunday) {
    timeBlocks.push(TimeBlock.Sunday);
  }

  // Public holiday
  if (publicHoliday) {
    timeBlocks.push(TimeBlock.PublicHoliday);
  }

  return timeBlocks;
}
